import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/firebase";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "../Context/AuthContext";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  FaSearch,
  FaTrashAlt,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaSave,
  FaTimes,
  FaGlassWhiskey,
} from "react-icons/fa";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./ReusableGuestList.scss";
import { useWeddingData } from "../hooks/useWeddingData";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";

const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";

const ReusableGuestList = ({
  title = "Guest List",
  guestType,
  collectionId,
  pageSize = 10,
  backTo = "/dashboard",
}) => {
  const { user } = useAuth();
  const { weddingData, loading: weddingLoading } = useWeddingData();

  const [familyDocs, setFamilyDocs] = useState([]);
  const [filteredFamilyDocs, setFilteredFamilyDocs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [editingFamilyId, setEditingFamilyId] = useState(null);
  const [editingFamilyName, setEditingFamilyName] = useState("");

  const [editingGuestKey, setEditingGuestKey] = useState(null);
  const [editingGuestName, setEditingGuestName] = useState("");
  const [editingLiquorKey, setEditingLiquorKey] = useState(null);
  const [editingLiquorValue, setEditingLiquorValue] = useState("No");

  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const navigate = useNavigate();

  const bride_name = weddingData?.bride_name || "Bride";
  const groom_name = weddingData?.groom_name || "Groom";
  const siteSlug = weddingData?.slug || "";

  const groomCollectionId = GROOM_COLLECTION;
  const brideCollectionId = BRIDE_COLLECTION;

  useEffect(() => {
    const loadTheme = async () => {
      if (weddingLoading) return;

      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);

        if (!weddingData?.slug) {
          setSiteTheme(fallbackTheme);
          return;
        }

        const siteRef = doc(db, "sites", weddingData.slug);
        const siteSnap = await getDoc(siteRef);

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading guest list theme:", error);
        setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
      } finally {
        setThemeLoading(false);
      }
    };

    loadTheme();
  }, [weddingData, weddingLoading]);

  const themeStyle = useMemo(() => {
    return createThemeStyle(siteTheme || DEFAULT_THEME);
  }, [siteTheme]);

  const resolvedCollectionId = useMemo(() => {
    const incoming = String(collectionId || "").toLowerCase().trim();

    if (incoming === "groom") return groomCollectionId;
    if (incoming === "bride") return brideCollectionId;

    if (incoming === groomCollectionId.toLowerCase()) return groomCollectionId;
    if (incoming === brideCollectionId.toLowerCase()) return brideCollectionId;

    return collectionId || "";
  }, [collectionId, groomCollectionId, brideCollectionId]);

  const resolvedTitle = useMemo(() => {
    if (collectionId === "groom" || resolvedCollectionId === groomCollectionId) {
      return `${groom_name}'s Guest List`;
    }

    if (collectionId === "bride" || resolvedCollectionId === brideCollectionId) {
      return `${bride_name}'s Guest List`;
    }

    return title;
  }, [
    collectionId,
    resolvedCollectionId,
    groomCollectionId,
    brideCollectionId,
    groom_name,
    bride_name,
    title,
  ]);

  const fetchGuestList = async () => {
    if (!user || !resolvedCollectionId) return;

    setLoading(true);

    try {
      const snapshot = await getDocs(
        collection(db, "users", user.uid, resolvedCollectionId)
      );

      const collected = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();

        collected.push({
          id: docSnap.id,
          familyName: data.familyName || "Unknown",
          guestType: data.guestType || "",
          guests: Array.isArray(data.guests) ? data.guests : [],
          createdAt: data.createdAt || null,
        });
      });

      collected.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );

      setFamilyDocs(collected);
      setFilteredFamilyDocs(collected);
    } catch (error) {
      console.error("Error fetching guests:", error);
      setFamilyDocs([]);
      setFilteredFamilyDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!weddingLoading) {
      fetchGuestList();
    }
  }, [user, resolvedCollectionId, weddingLoading]);

  useEffect(() => {
    setCurrentPage(0);
    setSelectedFamily(null);
    setEditingFamilyId(null);
    setEditingGuestKey(null);
    setEditingLiquorKey(null);
  }, [resolvedCollectionId]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredFamilyDocs(familyDocs);
      return;
    }

    const term = searchTerm.toLowerCase();

    const filtered = familyDocs.filter((familyDoc) => {
      const familyMatch = (familyDoc.familyName || "")
        .toLowerCase()
        .includes(term);

      const guestMatch = Array.isArray(familyDoc.guests)
        ? familyDoc.guests.some((guest) =>
            (guest.name || "").toLowerCase().includes(term)
          )
        : false;

      return familyMatch || guestMatch;
    });

    setFilteredFamilyDocs(filtered);
  }, [familyDocs, searchTerm]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(0);
    setSelectedFamily(null);
  };

  const familyCounts = useMemo(() => {
    return filteredFamilyDocs.map((familyDoc) => ({
      id: familyDoc.id,
      familyName: familyDoc.familyName || "Unknown",
      guestCount: Array.isArray(familyDoc.guests) ? familyDoc.guests.length : 0,
    }));
  }, [filteredFamilyDocs]);

  const totalFamilies = familyCounts.length;
  const totalGuests = filteredFamilyDocs.reduce(
    (sum, family) => sum + (Array.isArray(family.guests) ? family.guests.length : 0),
    0
  );

  const paginatedFamilyCounts = useMemo(() => {
    const start = currentPage * pageSize;
    return familyCounts.slice(start, start + pageSize);
  }, [familyCounts, currentPage, pageSize]);

  const selectedFamilyDoc = useMemo(() => {
    if (!selectedFamily) return null;
    return filteredFamilyDocs.find((g) => g.id === selectedFamily) || null;
  }, [filteredFamilyDocs, selectedFamily]);

  const selectedFamilyGuests = useMemo(() => {
    if (!selectedFamilyDoc) return [];

    return (selectedFamilyDoc.guests || []).map((guest, idx) => ({
      key: `${selectedFamilyDoc.id}-${idx}`,
      id: guest.id || `${selectedFamilyDoc.id}-${idx}`,
      name: guest.name || "",
      status: guest.status || "Pending",
      takingLiquor: guest.takingLiquor || "No",
      index: idx,
      displayIndex: idx + 1,
    }));
  }, [selectedFamilyDoc]);

  const selectedFamilyStats = useMemo(() => {
    if (!selectedFamilyDoc || !Array.isArray(selectedFamilyDoc.guests)) {
      return {
        total: 0,
        confirmed: 0,
        pending: 0,
        notParticipating: 0,
        liquorYes: 0,
      };
    }

    return selectedFamilyDoc.guests.reduce(
      (acc, guest) => {
        acc.total += 1;

        if (guest.status === "Participation Confirmed") acc.confirmed += 1;
        else if (guest.status === "Not Participating") acc.notParticipating += 1;
        else acc.pending += 1;

        if ((guest.takingLiquor || "No") === "Yes") acc.liquorYes += 1;

        return acc;
      },
      {
        total: 0,
        confirmed: 0,
        pending: 0,
        notParticipating: 0,
        liquorYes: 0,
      }
    );
  }, [selectedFamilyDoc]);

  const isFirstPage = currentPage === 0;
  const isLastPage = (currentPage + 1) * pageSize >= familyCounts.length;

  const handleNextPage = () => {
    if (!isLastPage) setCurrentPage((p) => p + 1);
  };

  const handlePrevPage = () => {
    if (!isFirstPage) setCurrentPage((p) => p - 1);
  };

  const handleGoBack = () => navigate(backTo);

  const handleHideGuests = () => {
    setSelectedFamily(null);
    setEditingFamilyId(null);
    setEditingGuestKey(null);
    setEditingLiquorKey(null);
  };

  const updateLocalFamilyDoc = (docId, updater) => {
    setFamilyDocs((prev) =>
      prev.map((item) => (item.id === docId ? updater(item) : item))
    );
  };

  const handleDeleteFamily = async (familyId, familyName, e) => {
    e?.stopPropagation();

    const ok = window.confirm(
      `Delete all guests in "${familyName}"? This cannot be undone.`
    );
    if (!ok || !user || !resolvedCollectionId) return;

    try {
      setActionLoading(true);

      const batch = writeBatch(db);

      batch.delete(doc(db, "users", user.uid, resolvedCollectionId, familyId));

      if (siteSlug) {
        batch.delete(doc(db, "sites", siteSlug, resolvedCollectionId, familyId));
      }

      await batch.commit();

      const newAll = familyDocs.filter((g) => g.id !== familyId);

      setFamilyDocs(newAll);

      if (selectedFamily === familyId) {
        setSelectedFamily(null);
      }
    } catch (err) {
      console.error("Error deleting family:", err);
      alert("Failed to delete family. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const startEditFamily = (familyDoc, e) => {
    e?.stopPropagation();
    setEditingFamilyId(familyDoc.id);
    setEditingFamilyName(familyDoc.familyName || "");
    setSelectedFamily(familyDoc.id);
  };

  const cancelEditFamily = () => {
    setEditingFamilyId(null);
    setEditingFamilyName("");
  };

  const saveFamilyName = async (familyDocId) => {
    const trimmedName = editingFamilyName.trim();

    if (!trimmedName) {
      alert("Family name cannot be empty.");
      return;
    }

    try {
      setActionLoading(true);

      const currentFamilyDoc = familyDocs.find((f) => f.id === familyDocId);
      if (!currentFamilyDoc) {
        alert("Family not found.");
        return;
      }

      const normalizedNewName = trimmedName.toLowerCase();

      const duplicateFamily = familyDocs.find(
        (family) =>
          family.id !== familyDocId &&
          String(family.familyName || "").trim().toLowerCase() === normalizedNewName
      );

      if (duplicateFamily) {
        alert(`A family named "${trimmedName}" already exists.`);
        return;
      }

      const privateRef = doc(db, "users", user.uid, resolvedCollectionId, familyDocId);
      await updateDoc(privateRef, { familyName: trimmedName });

      if (siteSlug) {
        const publicRef = doc(db, "sites", siteSlug, resolvedCollectionId, familyDocId);
        const publicSnap = await getDoc(publicRef);

        if (publicSnap.exists()) {
          await updateDoc(publicRef, { familyName: trimmedName });
        }
      }

      updateLocalFamilyDoc(familyDocId, (item) => ({
        ...item,
        familyName: trimmedName,
      }));

      setEditingFamilyId(null);
      setEditingFamilyName("");
    } catch (err) {
      console.error("Error updating family name:", err);
      alert("Failed to update family name. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const startEditGuest = (guest, e) => {
    e?.stopPropagation();
    setEditingGuestKey(guest.key);
    setEditingGuestName(guest.name || "");
  };

  const cancelEditGuest = () => {
    setEditingGuestKey(null);
    setEditingGuestName("");
  };

  const saveGuestName = async (familyDocId, guestIndex) => {
    const trimmedName = editingGuestName.trim();

    if (!trimmedName) {
      alert("Guest name cannot be empty.");
      return;
    }

    try {
      setActionLoading(true);

      const familyDoc = familyDocs.find((f) => f.id === familyDocId);
      if (!familyDoc) return;

      const updatedGuests = [...(familyDoc.guests || [])];
      updatedGuests[guestIndex] = {
        ...updatedGuests[guestIndex],
        name: trimmedName,
      };

      const privateRef = doc(db, "users", user.uid, resolvedCollectionId, familyDocId);
      await updateDoc(privateRef, { guests: updatedGuests });

      if (siteSlug) {
        const publicRef = doc(db, "sites", siteSlug, resolvedCollectionId, familyDocId);
        const publicSnap = await getDoc(publicRef);

        if (publicSnap.exists()) {
          await updateDoc(publicRef, { guests: updatedGuests });
        }
      }

      updateLocalFamilyDoc(familyDocId, (item) => ({
        ...item,
        guests: updatedGuests,
      }));

      setEditingGuestKey(null);
      setEditingGuestName("");
    } catch (err) {
      console.error("Error updating guest name:", err);
      alert("Failed to update guest name. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const startEditLiquor = (guest, e) => {
    e?.stopPropagation();
    setEditingLiquorKey(guest.key);
    setEditingLiquorValue(guest.takingLiquor || "No");
  };

  const cancelEditLiquor = () => {
    setEditingLiquorKey(null);
    setEditingLiquorValue("No");
  };

  const saveLiquorPreference = async (familyDocId, guestIndex) => {
    try {
      setActionLoading(true);

      const familyDoc = familyDocs.find((f) => f.id === familyDocId);
      if (!familyDoc) return;

      const updatedGuests = [...(familyDoc.guests || [])];
      updatedGuests[guestIndex] = {
        ...updatedGuests[guestIndex],
        takingLiquor: editingLiquorValue,
      };

      const privateRef = doc(db, "users", user.uid, resolvedCollectionId, familyDocId);
      await updateDoc(privateRef, { guests: updatedGuests });

      if (siteSlug) {
        const publicRef = doc(db, "sites", siteSlug, resolvedCollectionId, familyDocId);
        const publicSnap = await getDoc(publicRef);

        if (publicSnap.exists()) {
          await updateDoc(publicRef, { guests: updatedGuests });
        }
      }

      updateLocalFamilyDoc(familyDocId, (item) => ({
        ...item,
        guests: updatedGuests,
      }));

      setEditingLiquorKey(null);
      setEditingLiquorValue("No");
    } catch (err) {
      console.error("Error updating liquor preference:", err);
      alert("Failed to update liquor preference. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGuest = async (familyDocId, guestIndex, guestName) => {
    const ok = window.confirm(
      `Delete guest "${guestName}"? This cannot be undone.`
    );
    if (!ok) return;

    try {
      setActionLoading(true);

      const familyDoc = familyDocs.find((f) => f.id === familyDocId);
      if (!familyDoc) return;

      const updatedGuests = (familyDoc.guests || []).filter(
        (_, index) => index !== guestIndex
      );

      if (updatedGuests.length === 0) {
        const batch = writeBatch(db);

        batch.delete(doc(db, "users", user.uid, resolvedCollectionId, familyDocId));

        if (siteSlug) {
          batch.delete(doc(db, "sites", siteSlug, resolvedCollectionId, familyDocId));
        }

        await batch.commit();

        setFamilyDocs((prev) => prev.filter((item) => item.id !== familyDocId));
        if (selectedFamily === familyDocId) {
          setSelectedFamily(null);
        }
      } else {
        const privateRef = doc(db, "users", user.uid, resolvedCollectionId, familyDocId);
        await updateDoc(privateRef, { guests: updatedGuests });

        if (siteSlug) {
          const publicRef = doc(db, "sites", siteSlug, resolvedCollectionId, familyDocId);
          const publicSnap = await getDoc(publicRef);

          if (publicSnap.exists()) {
            await updateDoc(publicRef, { guests: updatedGuests });
          }
        }

        updateLocalFamilyDoc(familyDocId, (item) => ({
          ...item,
          guests: updatedGuests,
        }));
      }

      setEditingGuestKey(null);
      setEditingGuestName("");
      setEditingLiquorKey(null);
    } catch (err) {
      console.error("Error deleting guest:", err);
      alert("Failed to delete guest. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const exportToExcel = () => {
    const summaryData = [];
    const confirmedData = [];
    const pendingData = [];
    const notParticipatingData = [];
    const allGuestsData = [];

    let confirmedCount = 0;
    let pendingCount = 0;
    let notParticipatingCount = 0;

    filteredFamilyDocs.forEach((family) => {
      if (Array.isArray(family.guests)) {
        family.guests.forEach((guest) => {
          const entry = {
            "Family Name": family.familyName || "Unknown",
            "Guest Name": guest.name || "",
            Status: guest.status || "",
            "Taking Liquor": guest.takingLiquor || "No",
          };

          allGuestsData.push(entry);

          if (guest.status === "Participation Confirmed") {
            confirmedCount++;
            confirmedData.push(entry);
          } else if (guest.status === "Pending") {
            pendingCount++;
            pendingData.push(entry);
          } else if (guest.status === "Not Participating") {
            notParticipatingCount++;
            notParticipatingData.push(entry);
          }
        });
      }
    });

    summaryData.push(
      { Summary: "Confirmed Guests", Count: confirmedCount },
      { Summary: "Pending Guests", Count: pendingCount },
      { Summary: "Not Participating Guests", Count: notParticipatingCount }
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(confirmedData), "Confirmed Guests");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pendingData), "Pending Guests");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(notParticipatingData), "Not Participating");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(allGuestsData), "All Guests");

    const lowerGuestType = guestType?.toLowerCase() || "";
    const lowerBride = bride_name.toLowerCase();
    const lowerGroom = groom_name.toLowerCase();

    let personName = resolvedTitle;

    if (lowerGuestType.includes(lowerBride)) {
      personName = bride_name;
    } else if (lowerGuestType.includes(lowerGroom)) {
      personName = groom_name;
    }

    const safeTitle = `${personName}_Guest_List`
      .replace(/[^\w\- ]+/g, "")
      .replace(/\s+/g, "_");

    XLSX.writeFile(wb, `${safeTitle}.xlsx`);
  };

  if (!user || weddingLoading || themeLoading || !siteTheme) return null;

  return (
    <section className="guestList" aria-label="Guest list" style={themeStyle}>
      <div className="guestList__hero">
        <div>
          <p className="guestList__eyebrow">Guest Directory</p>
          <h1 className="guestList__title">{resolvedTitle}</h1>
        </div>

        <div className="guestList__heroActions">
          {/* <ButtonComponent className="is-ghost sm guestList__heroBtn" onClick={handleGoBack}>
            Back
          </ButtonComponent>

          <ButtonComponent className="xl guestList__heroBtn" onClick={exportToExcel}>
            Export to Excel
          </ButtonComponent> */}
        </div>
      </div>

      <div className="guestList__summaryGrid">
        <div className="guestList__summaryCard">
          <span className="guestList__summaryIcon"><FaUsers /></span>
          <div>
            <p className="guestList__summaryLabel">Families</p>
            <h3 className="guestList__summaryValue">{totalFamilies}</h3>
          </div>
        </div>

        <div className="guestList__summaryCard">
          <span className="guestList__summaryIcon"><FaUsers /></span>
          <div>
            <p className="guestList__summaryLabel">Guests</p>
            <h3 className="guestList__summaryValue">{totalGuests}</h3>
          </div>
        </div>
      </div>

      <div className="guestList__panel">
        <div className="guestList__searchWrap">
          <span className="guestList__searchIcon">
            <FaSearch />
          </span>
          <input
            type="text"
            className="guestList__searchInput"
            placeholder="Search by family name or guest name..."
            value={searchTerm}
            onChange={handleSearch}
            aria-label="Search guests"
            autoCorrect="off"
          />
        </div>

        <div className="guestList__desktopTableWrap">
          <table className="guestList__table">
            <thead>
              {familyCounts.length > 0 && (
                <tr className="guestList__headRow">
                  <th>Family Name</th>
                  <th>Guest Count</th>
                  <th>Action</th>
                </tr>
              )}
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" className="guestList__empty">
                    Loading...
                  </td>
                </tr>
              ) : familyCounts.length === 0 ? (
                <tr>
                  <td colSpan="3" className="guestList__empty">
                    {searchTerm ? "No guests found" : "No guests added"}
                  </td>
                </tr>
              ) : (
                paginatedFamilyCounts.map(({ id, familyName, guestCount }) => (
                  <tr
                    key={id}
                    className={`guestList__row ${selectedFamily === id ? "is-active" : ""}`}
                    onClick={() => setSelectedFamily(id)}
                  >
                    <td>
                      {editingFamilyId === id ? (
                        <div className="guestList__editInline" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            className="guestList__editInput"
                            value={editingFamilyName}
                            onChange={(e) => setEditingFamilyName(e.target.value)}
                            autoCorrect="off"
                          />
                          <div className="guestList__editActions">
                            <ButtonComponent
                              className="is-ghost sm"
                              onClick={() => saveFamilyName(id)}
                              disabled={actionLoading}
                            >
                              <FaSave style={{ marginRight: 6 }} />
                              Save
                            </ButtonComponent>
                            <ButtonComponent
                              className="is-ghost sm"
                              onClick={cancelEditFamily}
                              disabled={actionLoading}
                            >
                              <FaTimes style={{ marginRight: 6 }} />
                              Cancel
                            </ButtonComponent>
                          </div>
                        </div>
                      ) : (
                        <div className="guestList__familyNameBlock">
                          <span className="guestList__familyName">{familyName}</span>
                          <span className="guestList__familyMeta">Tap to manage guests</span>
                        </div>
                      )}
                    </td>

                    <td>
                      <span className="guestList__countBadge">{guestCount}</span>
                    </td>

                    <td>
                      <div className="guestList__tableActions">
                        <ButtonComponent
                          className="is-ghost sm"
                          onClick={(e) =>
                            startEditFamily(
                              familyDocs.find((f) => f.id === id),
                              e
                            )
                          }
                          disabled={actionLoading}
                        >
                          <FaEdit style={{ marginRight: 6 }} />
                          Edit
                        </ButtonComponent>

                        <ButtonComponent
                          className="is-danger sm"
                          onClick={(e) => handleDeleteFamily(id, familyName, e)}
                          disabled={actionLoading}
                        >
                          <FaTrashAlt style={{ marginRight: 6 }} />
                          Delete
                        </ButtonComponent>
                      </div>
                    </td>
                  </tr>
                ))
              )}

              {selectedFamilyDoc && (
                <tr className="guestList__detailRow">
                  <td colSpan="3" className="guestList__detailCell">
                    <div className="guestList__familyPanel guestList__familyPanel--inline">
                      <div className="guestList__familyTop guestList__familyTop--rich">
                        <div className="guestList__familyIntro">
                          <p className="guestList__familyEyebrow">Selected Family</p>
                          <h3 className="guestList__familyTitle">
                            {selectedFamilyDoc.familyName}&rsquo;s Guests
                          </h3>
                          <p className="guestList__familyDescription">
                            Manage guest names, liquor preferences, and remove entries from this family group.
                          </p>
                        </div>

                        <div className="guestList__familyTopActions">
                          <div className="guestList__miniStats">
                            <div className="guestList__miniStat">
                              <span className="guestList__miniStatLabel">Guests</span>
                              <strong>{selectedFamilyStats.total}</strong>
                            </div>
                            <div className="guestList__miniStat">
                              <span className="guestList__miniStatLabel">Confirmed</span>
                              <strong>{selectedFamilyStats.confirmed}</strong>
                            </div>
                            <div className="guestList__miniStat">
                              <span className="guestList__miniStatLabel">Liquor Yes</span>
                              <strong>{selectedFamilyStats.liquorYes}</strong>
                            </div>
                          </div>

                          <ButtonComponent
                            className="is-ghost sm"
                            onClick={handleHideGuests}
                          >
                            Hide Family
                          </ButtonComponent>
                        </div>
                      </div>

                      <div className="guestList__guestGrid">
                        {selectedFamilyGuests.map((guest) => (
                          <article
                            key={guest.key}
                            className="guestList__guestCard"
                          >
                            <div className="guestList__guestCardTop">
                              <div className="guestList__guestAvatar">
                                {(guest.name || "G").charAt(0).toUpperCase()}
                              </div>

                              <div className="guestList__guestCardMeta">
                                {editingGuestKey === guest.key ? (
                                  <div className="guestList__editInline guestList__editInline--guest">
                                    <input
                                      type="text"
                                      className="guestList__editInput"
                                      value={editingGuestName}
                                      onChange={(e) => setEditingGuestName(e.target.value)}
                                      autoCorrect="off"
                                    />
                                    <div className="guestList__editActions">
                                      <ButtonComponent
                                        className="is-ghost sm"
                                        onClick={() =>
                                          saveGuestName(selectedFamilyDoc.id, guest.index)
                                        }
                                        disabled={actionLoading}
                                      >
                                        <FaSave style={{ marginRight: 6 }} />
                                        Save
                                      </ButtonComponent>
                                      <ButtonComponent
                                        className="is-ghost sm"
                                        onClick={cancelEditGuest}
                                        disabled={actionLoading}
                                      >
                                        <FaTimes style={{ marginRight: 6 }} />
                                        Cancel
                                      </ButtonComponent>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <span className="guestList__guestName">{guest.name}</span>
                                    <span className="guestList__guestSub">
                                      Guest #{guest.displayIndex}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="guestList__guestTags">
                              <span
                                className={
                                  "guestList__status " +
                                  String(guest.status || "")
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")
                                }
                              >
                                {guest.status}
                              </span>

                              <span
                                className={`guestList__liquorBadge ${
                                  guest.takingLiquor === "Yes" ? "is-yes" : "is-no"
                                }`}
                              >
                                <FaGlassWhiskey />
                                {guest.takingLiquor === "Yes" ? "Liquor: Yes" : "Liquor: No"}
                              </span>
                            </div>

                            {editingLiquorKey === guest.key ? (
                              <div className="guestList__guestLiquorEditor">
                                <div className="guestList__liquorEditBox">
                                  <select
                                    className="guestList__editInput guestList__editSelect"
                                    value={editingLiquorValue}
                                    onChange={(e) => setEditingLiquorValue(e.target.value)}
                                  >
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                  </select>

                                  <div className="guestList__editActions">
                                    <ButtonComponent
                                      className="is-ghost sm"
                                      onClick={() =>
                                        saveLiquorPreference(selectedFamilyDoc.id, guest.index)
                                      }
                                      disabled={actionLoading}
                                    >
                                      <FaSave style={{ marginRight: 6 }} />
                                      Save
                                    </ButtonComponent>
                                    <ButtonComponent
                                      className="is-ghost sm"
                                      onClick={cancelEditLiquor}
                                      disabled={actionLoading}
                                    >
                                      <FaTimes style={{ marginRight: 6 }} />
                                      Cancel
                                    </ButtonComponent>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="guestList__guestActions guestList__guestActions--card">
                                <ButtonComponent
                                  className="is-ghost sm"
                                  onClick={(e) => startEditGuest(guest, e)}
                                  disabled={actionLoading}
                                >
                                  <FaEdit style={{ marginRight: 6 }} />
                                  Edit Name
                                </ButtonComponent>

                                <ButtonComponent
                                  className="is-ghost sm"
                                  onClick={(e) => startEditLiquor(guest, e)}
                                  disabled={actionLoading}
                                >
                                  <FaGlassWhiskey style={{ marginRight: 6 }} />
                                  Edit Liquor
                                </ButtonComponent>

                                <ButtonComponent
                                  className="is-danger sm"
                                  onClick={() =>
                                    handleDeleteGuest(
                                      selectedFamilyDoc.id,
                                      guest.index,
                                      guest.name
                                    )
                                  }
                                  disabled={actionLoading}
                                >
                                  <FaTrashAlt style={{ marginRight: 6 }} />
                                  Delete
                                </ButtonComponent>
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="guestList__mobileCards">
          {loading ? (
            <div className="guestList__emptyCard">Loading...</div>
          ) : familyCounts.length === 0 ? (
            <div className="guestList__emptyCard">
              {searchTerm ? "No guests found" : "No guests added"}
            </div>
          ) : (
            <div className="guestList__cardGrid">
              {paginatedFamilyCounts.map(({ id, familyName, guestCount }) => {
                const familyDoc = familyDocs.find((f) => f.id === id);

                return (
                  <article
                    key={id}
                    className={`guestList__familyCard ${selectedFamily === id ? "is-active" : ""}`}
                  >
                    <button
                      type="button"
                      className="guestList__familyCardMain"
                      onClick={() => setSelectedFamily(id)}
                    >
                      <div className="guestList__familyCardHead">
                        <span className="guestList__familyCardLabel">Family Name</span>
                        <span className="guestList__countBadge">{guestCount}</span>
                      </div>

                      {editingFamilyId === id ? (
                        <div className="guestList__editInline" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            className="guestList__editInput"
                            value={editingFamilyName}
                            onChange={(e) => setEditingFamilyName(e.target.value)}
                            autoCorrect="off"
                          />
                        </div>
                      ) : (
                        <h3 className="guestList__familyCardName">{familyName}</h3>
                      )}

                      <p className="guestList__familyCardMeta">Tap to manage guests</p>
                    </button>

                    <div className="guestList__familyCardActions">
                      {editingFamilyId === id ? (
                        <div className="guestList__tableActions">
                          <ButtonComponent
                            className="is-ghost sm guestList__cardBtn"
                            onClick={() => saveFamilyName(id)}
                            disabled={actionLoading}
                          >
                            <FaSave style={{ marginRight: 6 }} />
                            Save
                          </ButtonComponent>

                          <ButtonComponent
                            className="is-ghost sm guestList__cardBtn"
                            onClick={cancelEditFamily}
                            disabled={actionLoading}
                          >
                            <FaTimes style={{ marginRight: 6 }} />
                            Cancel
                          </ButtonComponent>
                        </div>
                      ) : (
                        <div className="guestList__tableActions">
                          <ButtonComponent
                            className="is-ghost sm guestList__cardBtn"
                            onClick={(e) => startEditFamily(familyDoc, e)}
                            disabled={actionLoading}
                          >
                            <FaEdit style={{ marginRight: 6 }} />
                            Edit
                          </ButtonComponent>

                          <ButtonComponent
                            className="is-danger sm guestList__cardBtn"
                            onClick={(e) => handleDeleteFamily(id, familyName, e)}
                            disabled={actionLoading}
                          >
                            <FaTrashAlt style={{ marginRight: 6 }} />
                            Delete
                          </ButtonComponent>
                        </div>
                      )}
                    </div>

                    {selectedFamily === id && selectedFamilyDoc?.id === id && (
                      <div className="guestList__familyPanel guestList__familyPanel--mobile">
                        <div className="guestList__familyTop guestList__familyTop--rich">
                          <div className="guestList__familyIntro">
                            <p className="guestList__familyEyebrow">Selected Family</p>
                            <h3 className="guestList__familyTitle">
                              {selectedFamilyDoc.familyName}&rsquo;s Guests
                            </h3>
                            <p className="guestList__familyDescription">
                              Edit names and liquor preferences below.
                            </p>
                          </div>

                          <ButtonComponent
                            className="is-ghost sm guestList__cardBtn"
                            onClick={handleHideGuests}
                          >
                            Hide Family
                          </ButtonComponent>
                        </div>

                        <div className="guestList__guestGrid guestList__guestGrid--mobile">
                          {selectedFamilyGuests.map((guest) => (
                            <article
                              key={guest.key}
                              className="guestList__guestCard guestList__guestCard--mobile"
                            >
                              <div className="guestList__guestCardTop">
                                <div className="guestList__guestAvatar">
                                  {(guest.name || "G").charAt(0).toUpperCase()}
                                </div>

                                <div className="guestList__guestCardMeta">
                                  {editingGuestKey === guest.key ? (
                                    <div className="guestList__editInline guestList__editInline--guest">
                                      <input
                                        type="text"
                                        className="guestList__editInput"
                                        value={editingGuestName}
                                        onChange={(e) => setEditingGuestName(e.target.value)}
                                        autoCorrect="off"
                                      />
                                      <div className="guestList__editActions">
                                        <ButtonComponent
                                          className="is-ghost sm guestList__cardBtn"
                                          onClick={() =>
                                            saveGuestName(selectedFamilyDoc.id, guest.index)
                                          }
                                          disabled={actionLoading}
                                        >
                                          <FaSave style={{ marginRight: 6 }} />
                                          Save
                                        </ButtonComponent>
                                        <ButtonComponent
                                          className="is-ghost sm guestList__cardBtn"
                                          onClick={cancelEditGuest}
                                          disabled={actionLoading}
                                        >
                                          <FaTimes style={{ marginRight: 6 }} />
                                          Cancel
                                        </ButtonComponent>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="guestList__guestName">{guest.name}</span>
                                      <span className="guestList__guestSub">
                                        Guest #{guest.displayIndex}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="guestList__guestTags">
                                <span
                                  className={
                                    "guestList__status " +
                                    String(guest.status || "")
                                      .toLowerCase()
                                      .replace(/\s+/g, "-")
                                  }
                                >
                                  {guest.status}
                                </span>

                                <span
                                  className={`guestList__liquorBadge ${
                                    guest.takingLiquor === "Yes" ? "is-yes" : "is-no"
                                  }`}
                                >
                                  <FaGlassWhiskey />
                                  {guest.takingLiquor === "Yes" ? "Liquor: Yes" : "Liquor: No"}
                                </span>
                              </div>

                              {editingLiquorKey === guest.key ? (
                                <div className="guestList__guestLiquorEditor">
                                  <div className="guestList__liquorEditBox">
                                    <select
                                      className="guestList__editInput guestList__editSelect"
                                      value={editingLiquorValue}
                                      onChange={(e) => setEditingLiquorValue(e.target.value)}
                                    >
                                      <option value="No">No</option>
                                      <option value="Yes">Yes</option>
                                    </select>

                                    <div className="guestList__editActions">
                                      <ButtonComponent
                                        className="is-ghost sm guestList__cardBtn"
                                        onClick={() =>
                                          saveLiquorPreference(selectedFamilyDoc.id, guest.index)
                                        }
                                        disabled={actionLoading}
                                      >
                                        <FaSave style={{ marginRight: 6 }} />
                                        Save
                                      </ButtonComponent>
                                      <ButtonComponent
                                        className="is-ghost sm guestList__cardBtn"
                                        onClick={cancelEditLiquor}
                                        disabled={actionLoading}
                                      >
                                        <FaTimes style={{ marginRight: 6 }} />
                                        Cancel
                                      </ButtonComponent>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="guestList__guestActions guestList__guestActions--mobile">
                                  <ButtonComponent
                                    className="is-ghost sm guestList__cardBtn"
                                    onClick={(e) => startEditGuest(guest, e)}
                                    disabled={actionLoading}
                                  >
                                    <FaEdit style={{ marginRight: 6 }} />
                                    Edit Name
                                  </ButtonComponent>

                                  <ButtonComponent
                                    className="is-ghost sm guestList__cardBtn"
                                    onClick={(e) => startEditLiquor(guest, e)}
                                    disabled={actionLoading}
                                  >
                                    <FaGlassWhiskey style={{ marginRight: 6 }} />
                                    Edit Liquor
                                  </ButtonComponent>

                                  <ButtonComponent
                                    className="is-danger sm guestList__cardBtn"
                                    onClick={() =>
                                      handleDeleteGuest(
                                        selectedFamilyDoc.id,
                                        guest.index,
                                        guest.name
                                      )
                                    }
                                    disabled={actionLoading}
                                  >
                                    <FaTrashAlt style={{ marginRight: 6 }} />
                                    Delete
                                  </ButtonComponent>
                                </div>
                              )}
                            </article>
                          ))}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <div className="guestList__pagination">
          {!isFirstPage && (
            <ButtonComponent className="is-ghost guestList__pageBtn" onClick={handlePrevPage}>
              <FaChevronLeft style={{ marginRight: 6 }} />
              Previous
            </ButtonComponent>
          )}
          {!isLastPage && (
            <ButtonComponent className="guestList__pageBtn" onClick={handleNextPage}>
              Next
              <FaChevronRight style={{ marginLeft: 6 }} />
            </ButtonComponent>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReusableGuestList;